import { FC, useState } from "react";
import { Box, LinearProgress, Slider, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { formatPlayerTime } from "./formatPlayerTime";
import { TimecodeMarkers } from "./TimecodeMarkers";
import type { PlayerTimecode } from "./playerTimecode";

/** Reference SeekBarPopup without sprite previews: an 80x32 dark box above
 * the bar, kept inside the bar by clamping its centre to [40, width-40]
 * (MUI's own value label was centred on the thumb and got cut off at the
 * left edge on "1:04:35"). */
const POPUP_WIDTH = 80;
const POPUP_HEIGHT = 32;
const POPUP_MARGIN = 5;
/** Horizontal padding of the root box: the slider spans the inner width. */
const BAR_PADDING = 8;

interface SeekBarProps {
  currentTime: number;
  duration: number;
  buffered: number;
  onSeek: (time: number) => void;
  /** Метки таймлайна (MARKER) — иконки поверх сикбара. */
  timecodes?: PlayerTimecode[] | null;
}

export const SeekBar: FC<SeekBarProps> = ({
  currentTime,
  duration,
  buffered,
  onSeek,
  timecodes,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [hover, setHover] = useState<{ x: number; width: number } | null>(null);
  const [dragValue, setDragValue] = useState<number | null>(null);
  const hoverTime =
    hover && hover.width > 0 ? (hover.x / hover.width) * duration : null;
  // While dragging the popup follows the thumb, not the cursor.
  const popupTime = dragValue ?? hoverTime;
  const popupX =
    hover && hover.width > 0 && popupTime != null && duration > 0
      ? Math.min(
          Math.max(
            dragValue != null ? (dragValue / duration) * hover.width : hover.x,
            POPUP_WIDTH / 2,
          ),
          hover.width - POPUP_WIDTH / 2,
        )
      : null;
  const bufferedPercent =
    duration > 0 ? Math.min(100, (buffered / duration) * 100) : 0;
  const sliderValue = dragValue ?? (duration > 0 ? currentTime : 0);

  return (
    <Box
      sx={{
        position: "relative",
        px: 1,
        height: 24,
        display: "flex",
        alignItems: "center",
      }}
      onMouseMove={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const width = rect.width - BAR_PADDING * 2;
        const x = Math.max(
          0,
          Math.min(width, event.clientX - rect.left - BAR_PADDING),
        );
        setHover({ x, width });
      }}
      onMouseLeave={() => setHover(null)}
    >
      {!isMobile && popupX != null && popupTime != null && (
        <Box
          sx={{
            position: "absolute",
            pointerEvents: "none",
            left: BAR_PADDING + popupX - POPUP_WIDTH / 2,
            bottom: 24 + POPUP_MARGIN,
            width: POPUP_WIDTH,
            height: POPUP_HEIGHT,
            lineHeight: `${POPUP_HEIGHT}px`,
            textAlign: "center",
            color: "#fff",
            fontSize: "0.875rem",
            background: "rgba(31, 31, 31, 0.85)",
            borderRadius: "4px",
          }}
        >
          {formatPlayerTime(popupTime)}
        </Box>
      )}
      <LinearProgress
        variant="determinate"
        value={bufferedPercent}
        sx={{
          position: "absolute",
          left: 8,
          right: 8,
          height: 4,
          borderRadius: 2,
          backgroundColor: "transparent",
          "& .MuiLinearProgress-bar": {
            backgroundColor: "rgba(255,255,255,0.2)",
          },
        }}
      />
      {/* No `size="small"` here on purpose: the reference slider is
          default/medium-sized too (it only shrinks the thumb via its own
          class override), which keeps the thumb's default resting
          drop-shadow (`theme.shadows[2]`, suppressed by MUI's small-size
          variant) and its default 42px hit target. Only the visible
          dimensions are overridden below, matching reference exactly. */}
      <Slider
        value={sliderValue}
        min={0}
        max={duration || 1}
        step={0.1}
        onChange={(_event, value) => setDragValue(value as number)}
        onChangeCommitted={(_event, value) => {
          setDragValue(null);
          onSeek(value as number);
        }}
        sx={{
          position: "relative",
          padding: "11px 0",
          // Reference never sets rail opacity itself — it relies on MUI's
          // own default (0.38) to dim the unplayed portion against the
          // played track/thumb. Our previous round explicitly forced
          // opacity:1 here, which killed that dimming and made the whole
          // bar read as one flat color; pin 0.38 explicitly instead of
          // silently depending on the MUI default.
          "& .MuiSlider-rail": {
            opacity: 0.38,
            height: 4,
            backgroundColor: "primary.dark",
          },
          "& .MuiSlider-track": {
            height: 4,
            backgroundColor: "primary.dark",
            border: "none",
          },
          "& .MuiSlider-thumb": {
            width: 10,
            height: 10,
            backgroundColor: "#fff",
          },
        }}
      />
      <TimecodeMarkers
        timecodes={timecodes}
        duration={duration}
        onTimecodeClick={onSeek}
      />
    </Box>
  );
};
